import type { ReactNode } from 'react';

import type { FormIdentity } from '../../types';

/**
 * Hero banner above the section stack. Light-blue panel with the form's
 * "front matter" — Core Model + stream pills, title, short description,
 * and action buttons on the right (Test Ticket etc.). Mirrors the original
 * standalone app's design-doc feel.
 *
 * No sys_ids or technical IDs. Buttons come from the parent so this stays
 * a presentational shell.
 */
export function FormHero({
  identity,
  actions,
}: {
  identity: FormIdentity;
  actions?: ReactNode;
}) {
  const stream = identity.stream;
  return (
    <header className="rounded-xl bg-brand-50/70 ring-1 ring-brand-100 px-6 sm:px-8 py-6 sm:py-7 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={
                'inline-flex items-center text-[11px] font-semibold uppercase tracking-wide rounded-full px-2.5 py-0.5 ' +
                (identity.is_core
                  ? 'bg-brand-100 text-brand-danone'
                  : 'bg-amber-100 text-amber-800')
              }
            >
              {identity.is_core ? 'Core Model' : 'Non-core'}
            </span>
            {stream && (
              <span className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide">
                {stream}
              </span>
            )}
          </div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-brand-danone leading-tight break-words">
            {identity.name || 'Untitled form'}
          </h1>
          {identity.category && (
            <div className="mt-1 text-sm text-slate-600 font-medium">
              {identity.category}
            </div>
          )}
          {identity.short_description && (
            <p className="mt-3 text-sm text-slate-700 leading-relaxed max-w-3xl">
              {identity.short_description}
            </p>
          )}
        </div>
        {actions && (
          <div className="shrink-0 flex flex-col items-stretch gap-2 sm:w-56 [&>*]:w-full [&>*]:justify-center">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
